<template>
  <v-input
    v-bind="$attrs"
    :value="value"
    :disabled="$attrs.disabled"
    class="p-file-input"
    :class="{ 'p-file-input--has-files': files.length }"
  >
    <div class="p-file-input__slot">
      <label
        v-if="label"
        v-text="label"
        class="p-label v-label"
      ></label>
      <input
        type="file"
        ref="fileInput"
        :accept="accept"
        :multiple="multiple"
        @click="onInputClick"
        @change="onInputChange"
      />
      <div class="p-file-input__dropzone"
        :class="{ 'p-dragover': dragover }"
        :style="{ 'min-height': height + 'px'}"
        @dragenter.prevent="dragover = true"
        @dragover.prevent="dragover = true"
        @dragleave.prevent="dragover = false"
        @drop.prevent="onDrop($event)"
        @click="$refs.fileInput.click()"
      >
        <div
          v-if="clearable !== false && files.length"
          class="p-file-input__clear-icon"
          @click.stop="clearFiles"
        >
          <v-icon>mdi-close</v-icon>
        </div>

        <div class="p-file-input__placeholder">
          <v-icon v-if="!files.length" :size="30">
            mdi-cloud-upload-outline
          </v-icon>
          <div>
            {{ $t('components.fileInput.placeholder.drop') + (libraryEnabled !== false ? ',' : ' ' + $t('components.fileInput.placeholder.or')) }}
            <span
              v-text="$t('components.fileInput.placeholder.browse')"
              class="p-file-input__placeholder-link"
            ></span>
            <template v-if="libraryEnabled !== false">
              {{ $t('components.fileInput.placeholder.or') }}
              <span
                v-text="$t('components.fileInput.placeholder.selectFromLibrary')"
                class="p-file-input__placeholder-link"
                @click.stop="fileInputDialog = true"
              ></span>
            </template>
          </div>
        </div>

        <div v-if="showReorderHint !== false && files.length > 1" class="p-file-input__drag-hint">
          {{ $t('components.fileInput.reorderHint') }}
        </div>

        <draggable
          v-if="files.length"
          v-model="files"
          class="p-file-input__selection"
          ghost-class="p-visibility-hidden"
        >
          <div
            v-for="(file, index) in files"
            :key="`file-${index}-${file.id}`"
            :set="fileGuid = getFileGuid(file)"
          >
            <div
              class="p-file-input__selection-item"
              :class="{ 'p-file-input__selection-item--error': uploadStatusByGuid[fileGuid] === uploadStatus.error }"
              :style="{ width: filePreviewSize + 14 + 'px'}"
              @click.stop
            >
              <p-file-preview
                :file="file"
                :height="filePreviewSize"
                :width="filePreviewSize"
                class="mx-auto"
              ></p-file-preview>

              <label
                v-text="file.name"
                :title="file.name"
                class="p-file-input__selection-item-name"
              ></label>

              <div v-if="primaryEnabled !== false && index === 0" class="p-file-input__selection-item-primary">
                {{ primaryText || $t('components.fileInput.primary') }}
              </div>

              <v-icon
                v-if="file.id"
                size="12"
                color="white"
                class="p-file-input__selection-item-status"
              >
                mdi-check
              </v-icon>

              <div
                v-if="uploadStatusByGuid[fileGuid] !== uploadStatus.progress"
                class="p-file-input__selection-item-remove"
                @click.stop="removeFile(index)"
              >
                <v-icon color="white" size="15">mdi-close</v-icon>
              </div>

              <v-progress-linear
                v-if="uploadStatusByGuid[fileGuid] === uploadStatus.progress"
                indeterminate
                color="primary"
                class="p-file-input__selection-item-progress"
              ></v-progress-linear>
            </div>
          </div>
        </draggable>
      </div>
      <div v-if="errorMessages.length">
        <div v-for="errorMessage in errorMessages" :key="`errorMessage-${errorMessage.name}`" class="mt-6">
          <span class="error--text">{{ errorMessage.text }}</span>
        </div>
      </div>
    </div>

    <p-file-input-dialog
      :dialog.sync="fileInputDialog"
      :selected-files="files"
      :file-type="libraryFileType"
      :multiple="multiple"
      @submit="onFileInputDialogSubmit"
    >
    </p-file-input-dialog>
  </v-input>
</template>


<script>
import Vue from 'vue'
import draggable from 'vuedraggable'
import { mapActions } from 'vuex'

import PFileInputDialog from './PFileInputDialog'
import PFilePreview from './PFilePreview'


export default {
  inheritAttrs: false,


  components: {
    draggable,
    PFileInputDialog,
    PFilePreview
  },


  props: {
    value: {
      type: Array
    },
    accept: {
      type: String,
      default: '*'
    },
    multiple: {
      type: Boolean,
      default: false
    },
    label: {
      type: String
    },
    cssClass: {
      type: undefined
    },
    height: {
      type: Number,
      default: 150
    },
    primaryEnabled: {
      type: Boolean,
      default: false
    },
    primaryText: {
      type: String
    },
    showReorderHint: {
      type: Boolean,
      default: false
    },
    libraryEnabled: {
      type: Boolean,
      default: false
    },
    libraryFileType: {
      type: Number
    },
    filePreviewSize: {
      type: Number,
      default: 100
    },
    clearable: {
      type: Boolean,
      default: false
    },
    isMarketing: {
      type: Boolean,
      default: false
    }
  },


  data () {
    return {
      dragover: false,
      fileInputDialog: false,
      uploadStatusByGuid: {},
      uploadStatus: {
        progress: 1,
        success: 2,
        error: 3
      },
      errorMessages: []
    }
  },


  computed: {
    files: {
      get () {
        return this.value || []
      },
      set (val) {
        this.emitValue(val)
      }
    }
  },


  methods: {
    ...mapActions('file', ['uploadFile']),

    onDrop (event) {
      this.dragover = false
      const files = Array.from(event.dataTransfer.files)
      this.addFiles(files)
    },

    onInputClick (event) {
      event.target.value = null
    },

    fileSizeInMb (sizeInBites) {
      return (sizeInBites / 1024 / 1024).toFixed(2)
    },

    onInputChange (event) {
      const files = Array.from(event.target.files)
      this.addFiles(files)
    },

    onFileInputDialogSubmit (value) {
      const selectedFiles = Array.isArray(value) ? value : [value]

      if (this.multiple === false && !value.length) {
        this.files = []
      } else {
        for (const [index, file] of this.files.entries()) {
          if (file.id && !selectedFiles.some(x => x.id === file.id)) {
            this.files.splice(index, 1)
          }
        }
      }

      this.addFiles(selectedFiles)
    },

    formErrorMessages () {
      this.errorMessages = []

      this.files.forEach(file => {
        if (file.size && this.isImage(file) && this.isLargerThenMb(file, 2)) {
          this.errorMessages.push({
            name: file.name,
            text: this.$t('validation.imageIsLargerThenMb', { name: file.name, mb: 2, size: this.fileSizeInMb(file.size) })
          })
          return false
        }
      })
    },

    addFiles (inputFiles) {
      if (!inputFiles.length) return

      if (this.multiple === false) {
        this.files = [inputFiles[0]]
      } else {
        for (const inputFile of inputFiles) {
          if (!this.isFileSelected(inputFile)) {
            this.files.push(inputFile)
          }
        }
      }

      this.formErrorMessages()

      if (this.isMarketing) {
        this.emitValue(this.files)
      }
    },

    isImage (file) {
      return file?.type?.startsWith('image')
    },

    isLargerThenMb (file, megabites) {
      return file?.size > megabites * 1024 * 1024
    },

    getImageUrl (file) {
      return URL.createObjectURL(file)
    },

    removeFile (index) {
      this.files.splice(index, 1)
      this.formErrorMessages()
      this.emitValue(this.files)
    },

    clearFiles () {
      this.files = []
      this.errorMessages = []
    },

    emitValue (value) {
      const files = value || []
      this.$emit('input', files)
      this.$emit('change', files)
    },

    isFileSelected (file) {
      const fileGuid = this.getFileGuid(file)
      return this.files.some(x => this.getFileGuid(x) === fileGuid)
    },

    getFileGuid (file) {
      return file.id || `${file.name}${file.size}${file.lastModified}`
    },

    async uploadFiles (projectId, fileType) {
      const uploadTasks = []
      let success = true

      for (const [index, file] of this.files.entries()) {
        if (!file.id) {
          const fileGuid = this.getFileGuid(file)
          Vue.set(this.uploadStatusByGuid, fileGuid, this.uploadStatus.progress)
          const task = this.uploadFile({ projectId, fileType, file })
            .then(result => {
              this.files[index] = result
              Vue.set(this.uploadStatusByGuid, fileGuid, this.uploadStatus.success)
            })
            .catch(error => {
              success = false
              Vue.set(this.uploadStatusByGuid, fileGuid, this.uploadStatus.error)
              this.$log.error(error)
            })

          uploadTasks.push(task)
        }
      }
      await Promise.all(uploadTasks)

      return success
    }
  }
}
</script>
